/**
 * Sanitize a `destination` query param before using it as a navigation target
 * (in /login and /redirect).
 *
 * Guards against the self-wrapping redirect loop that took down /identity/login:
 * `AuthGate` (in @side/next-auth) sets `destination` to the *entire* current URL
 * with no cap, so a login-pointing or already-nested destination could compound
 * under a redirect cycle (driven by the embed's flapping session) until the URL
 * exceeded server limits and returned 500. This helper makes that impossible:
 *   - rejects absurdly long values (the explosive nested case) outright;
 *   - reduces any input to its pathname + search, discarding the origin, so
 *     absolute or `//host` URLs collapse to a safe same-origin path (preserving
 *     the long-standing /redirect contract — see redirect.spec.ts);
 *   - never returns a `/identity/login` destination — it recursively unwraps a
 *     login-wrapped destination down to the innermost real path, or drops it.
 *
 * Returns a safe relative path, or undefined if there's no usable destination.
 */
const LOGIN_PATH = '/identity/login';
const MAX_DESTINATION_LENGTH = 2048;
const MAX_UNWRAP = 10;

export function sanitizeDestination(
  raw: string | string[] | undefined,
): string | undefined {
  let value = Array.isArray(raw) ? raw[0] : raw;
  // Drop missing or explosively long destinations (the nested-loop case).
  if (!value || value.length > MAX_DESTINATION_LENGTH) {
    return undefined;
  }

  for (let i = 0; i < MAX_UNWRAP; i += 1) {
    let url: URL;
    try {
      url = new URL(value, 'http://localhost');
    } catch {
      return undefined;
    }

    // Always reduce to pathname + search; the origin is discarded, so absolute
    // or protocol-relative inputs collapse to a safe same-origin path (this
    // preserves the long-standing /redirect contract — see redirect.spec.ts).
    if (url.pathname !== LOGIN_PATH) {
      return `${url.pathname}${url.search}`;
    }

    // Login-pointing destination: never navigate to it (that's the loop).
    // Peel the inner destination and re-check; drop entirely if there is none.
    const inner = url.searchParams.get('destination');
    if (!inner) {
      return undefined;
    }
    value = inner;
  }

  // Nested deeper than MAX_UNWRAP — treat as malicious/looping and drop.
  return undefined;
}
